export const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
  { value: 'MX', label: 'Mexico' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'GB', label: 'United Kingdom' },
];

/** US state code → full name (FedEx wants the code). */
const US_STATE_NAME_BY_CODE: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
  PR: 'Puerto Rico',
  VI: 'Virgin Islands',
  GU: 'Guam',
  AS: 'American Samoa',
  MP: 'Northern Mariana Islands',
  AA: 'Armed Forces Americas',
  AE: 'Armed Forces Europe',
  AP: 'Armed Forces Pacific',
};

const CA_PROVINCE_NAME_BY_CODE: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

const AU_STATE_NAME_BY_CODE: Record<string, string> = {
  ACT: 'Australian Capital Territory',
  NSW: 'New South Wales',
  NT: 'Northern Territory',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  VIC: 'Victoria',
  WA: 'Western Australia',
};

/** NZ/GB/DE postal address formats do not require an administrative area. */
export function isStateRequired(country?: string | null): boolean {
  return !['NZ', 'GB', 'DE'].includes(normalizeCountryCode(country) ?? '');
}

function stateNamesForCountry(
  country?: string | null
): Record<string, string> | undefined {
  switch (normalizeCountryCode(country)) {
    case 'US':
      return US_STATE_NAME_BY_CODE;
    case 'CA':
      return CA_PROVINCE_NAME_BY_CODE;
    case 'AU':
      return AU_STATE_NAME_BY_CODE;
    default:
      return undefined;
  }
}

/** Normalize region names using the selected country; retain legacy US/CA behavior without one. */
export function normalizeStateCode(
  value: string | null | undefined,
  country?: string | null
): string | undefined {
  const stripped = value?.trim();
  if (!stripped) return undefined;
  const upper = stripped.toUpperCase();
  const countryMap = stateNamesForCountry(country);
  const maps = country
    ? countryMap
      ? [countryMap]
      : []
    : [US_STATE_NAME_BY_CODE, CA_PROVINCE_NAME_BY_CODE];
  for (const map of maps) {
    if (upper in map) return upper;
  }
  const lower = stripped.toLowerCase();
  for (const map of maps) {
    for (const [code, name] of Object.entries(map)) {
      if (name.toLowerCase() === lower) return code;
    }
  }
  return stripped;
}

/** Check membership only for countries with a maintained region list. */
export function isValidStateForCountry(
  value: string | null | undefined,
  country?: string | null
): boolean {
  const map = stateNamesForCountry(country);
  const state = normalizeStateCode(value, country);
  return !map || Boolean(state && Object.hasOwn(map, state));
}

/** Normalize country to ISO-ish 2-letter codes FedEx expects. */
export function normalizeCountryCode(
  value: string | null | undefined
): string | undefined {
  const stripped = value?.trim();
  if (!stripped) return undefined;
  const upper = stripped.toUpperCase();
  const aliases: Record<string, string> = {
    AUS: 'AU',
    AUSTRALIA: 'AU',
    NZL: 'NZ',
    'NEW ZEALAND': 'NZ',
    US: 'US',
    USA: 'US',
    'UNITED STATES': 'US',
    'UNITED STATES OF AMERICA': 'US',
    CA: 'CA',
    CAN: 'CA',
    CANADA: 'CA',
    MX: 'MX',
    MEX: 'MX',
    MEXICO: 'MX',
    GB: 'GB',
    UK: 'GB',
    'UNITED KINGDOM': 'GB',
    DE: 'DE',
    GERMANY: 'DE',
  };
  return aliases[upper] || (upper.length === 2 ? upper : stripped);
}

/** Sorted labels and carrier codes share the same data as normalization and validation. */
export function getStateOptions(
  country?: string | null
): { value: string; label: string }[] | undefined {
  const map = stateNamesForCountry(country);
  return map
    ? Object.entries(map)
        .map(([value, label]) => ({ value, label: `${label} (${value})` }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : undefined;
}
